import type {
  BodyMeasurementAxisRange,
  BodyMeasurementTypeKey,
  BodyMeasurementUnit,
} from "./body-measurements";
import type { DayKey } from "./days";

export interface WidgetConfigBase {
  id: string;
  kind: string;
  label: string;
  row: number;
  column: number;
  enabled: boolean;
}

// The two reset modes read their tasks completely differently, so the task
// shape splits with the mode (CONTEXT.md: "Checkable from"). Under daily
// reset `visibleDays` IS the day's scoring set. Under weekly reset there is
// one shared bit per task, and the only per-day question is when its box
// opens — a single day, not a set.
export interface DailyRecurringTask {
  id: string;
  label: string;
  visibleDays: DayKey[];
}

export interface WeeklyRecurringTask {
  id: string;
  label: string;
  checkableFrom: DayKey;
}

export type RecurringTask = DailyRecurringTask | WeeklyRecurringTask;

interface RecurringTasksConfigBase extends WidgetConfigBase {
  kind: "recurring-tasks";
  points: number;
  weekGoal: number;
}

export interface DailyRecurringTasksConfig extends RecurringTasksConfigBase {
  reset: "daily";
  tasks: DailyRecurringTask[];
}

export interface WeeklyRecurringTasksConfig extends RecurringTasksConfigBase {
  reset: "weekly";
  tasks: WeeklyRecurringTask[];
}

export type RecurringTasksConfig =
  | DailyRecurringTasksConfig
  | WeeklyRecurringTasksConfig;

export interface WeeklyPointSummaryRow {
  id: string;
  name: string;
  widgetIds: string[];
}

export interface WeeklyPointSummaryConfig extends WidgetConfigBase {
  kind: "weekly-point-summary";
  rows: WeeklyPointSummaryRow[];
}

/**
 * One item of a huskeliste. Both ordinals are optional and both are absent on
 * every todo written before they existed; `src/lib/todos.ts` owns every
 * transition between the states they encode (docs/adr/0019, docs/adr/0020).
 */
export interface Todo {
  id: string;
  label: string;
  checked: boolean;
  /** Fullføringsrekkefølge: minted on tick, dropped on untick. */
  completionOrder?: number;
  /** Presence is the tombstone. Dropped on restore. */
  deletionOrder?: number;
}

export interface TodoListConfig extends WidgetConfigBase {
  kind: "todo-list";
}

export interface Workout {
  id: string;
  label: string;
  // The Planlagt økt this workout was ticked from (docs/adr/0025). Its
  // presence is the only thing that makes that plan kept — nothing on the
  // plan records it. Absent on every workout logged by hand.
  plannedWorkoutId?: string;
}

// «Planlegging» (CONTEXT.md: Planlagt uke). Absent means switched off, which
// is also how every config and snapshot written before planning existed
// reads, so none of them needs a migration.
export interface WorkoutPlanningConfig {
  points: number;
  // Planleggingsfrist: the last weekday on which saving the plan still earns
  // `points`, or null when it earns on any day.
  deadline: DayKey | null;
  weekGoal: number;
}

export interface WorkoutLogConfig extends WidgetConfigBase {
  kind: "workout-log";
  points: number;
  // With planning off, the Økter goal and the whole instance's goal are one
  // number. With it on, the instance has two Scorable parts (docs/adr/0010)
  // and this is derived: always workoutsWeekGoal + planning.weekGoal.
  weekGoal: number;
  // The Økter part's goal. Read only while planning is on; absent on configs
  // that have never had it, where it is `weekGoal`.
  workoutsWeekGoal?: number;
  planning?: WorkoutPlanningConfig;
}

export interface CounterConfig extends WidgetConfigBase {
  kind: "counter";
  points: number;
  // Daily points cap — a literal clamp on the score, never on the count.
  maxPoints: number;
  weekGoal: number;
}

export interface SleepLogConfig extends WidgetConfigBase {
  kind: "sleep-log";
  // Wall-clock "HH:MM" goals, one per half. Both halves always score, so
  // there is no per-half enable flag (CONTEXT.md: "Søvn").
  asleepTarget: string;
  awakeTarget: string;
  // Shared by both halves — one set of numbers, two targets. `points` and
  // `reductionPerInterval` are held to a quarter-point grid by validate, so
  // every score in the app stays exact in floating point and no total ever
  // needs rounding.
  points: number;
  intervalMinutes: number;
  reductionPerInterval: number;
  weekGoal: number;
}

export interface WorkdayLogConfig extends WidgetConfigBase {
  kind: "workday-log";
  // Both are "HH:MM" *durations*, not wall-clock times, and they are
  // independent knobs (CONTEXT.md: "Periode"): a 07:30 day against 03:30
  // periods earns its two periods with half an hour to spare, which is the
  // point — shortening the period to 02:30 makes a full day earn three.
  targetDuration: string;
  periodDuration: string;
  // Held to a quarter grid by validate. The hours score is floored to the
  // same grid, so every score this widget produces is exact in floating
  // point and no display site needs a rounding rule.
  pointsAtTarget: number;
  pointsPerPeriod: number;
  // One goal per scorable part (docs/adr/0010), so a weekly-point-summary row
  // can follow the hours without the productivity or the other way round.
  // Both required; both uncapped, so validate skips the goal-versus-max check
  // (docs/adr/0003).
  hoursWeekGoal: number;
  periodsWeekGoal: number;
  // Derived: always the sum of the two above, maintained on write rather than
  // typed. DayStrip's week tile and any whole-widget summary reference read
  // this field, so it has to stay a true total; NaN while either part goal is
  // still blank, which is what blocks Save.
  weekGoal: number;
}

export const MEAL_METRICS = [
  "kcal",
  "protein",
  "carbs",
  "fat",
  "fiber",
] as const;

export type MealMetric = (typeof MEAL_METRICS)[number];

export interface MealCondition {
  id: string;
  metric: MealMetric;
  direction: "atMost" | "atLeast";
  threshold: number;
  points: number;
}

export interface MealLogConfig extends WidgetConfigBase {
  kind: "meal-log";
  conditions: MealCondition[];
  // Required (finite, > 0) iff conditions is non-empty; absent in pure tracking.
  weekGoal?: number;
}

export interface Ingredient extends Record<MealMetric, number> {
  name: string;
  grams: number;
}

export interface ChatMessage {
  role: "user" | "assistant";
  text: string;
  // Auto-recorded table edit ("Fjernet Melk.") — rendered as an action line.
  event?: true;
}

// How an eaten amount was expressed (docs/adr/0006). `recipe` is the whole
// ingredient list; `grams` measures against the finished weight; `portions`
// against the declared portion count.
export type AmountUnit = "grams" | "recipe" | "portions";

// The amount as the user typed it — value and unit are both kept so a meal
// logged at 85 g reopens as 85 g rather than 0.2005.
export interface ComponentAmount {
  unit: AmountUnit;
  value: number; // finite, > 0
}

// A recipe's ingredients copied into a meal at import (docs/adr/0005).
// Read-only block: adjusted only via `amount` or by deleting the whole
// component.
export interface RecipeComponent {
  id: string;
  name: string;
  // Provenance only — nothing ever resolves it; a deleted recipe leaves
  // past meals untouched.
  recipeId?: string;
  // Absent ≡ { unit: "recipe", value: portion ?? 1 } — components saved
  // before units existed.
  amount?: ComponentAmount;
  // Superseded by `amount`; still read so old meals keep their fraction.
  portion?: number; // finite, > 0
  // The recipe's declared facts, copied at import so a later recipe edit
  // never re-interprets a logged day (docs/adr/0006). Absent ≡ Σ
  // ingredients.grams / 1.
  finishedGrams?: number; // finite, > 0
  portions?: number; // whole number ≥ 1
  ingredients: Ingredient[];
}

export interface Meal extends Record<MealMetric, number> {
  id: string;
  name: string;
  // Estimated-meal fields, stripped together when the day is finished
  // (docs/adr/0001) or the meal converts to manual — a meal without them
  // is a manual meal. `ingredients` is the chat component at reference
  // scale; `scale` its eaten fraction (absent ≡ 1). Totals above =
  // round(scale × Σ ingredients + Σ recipeComponents' scaled sums).
  ingredients?: Ingredient[];
  // The chat component has no finished weight or portion count of its own,
  // so it takes a bare fraction rather than a ComponentAmount.
  scale?: number; // finite, > 0; absent ≡ 1
  // Former name for `scale`; still read so old meals keep their fraction.
  portion?: number;
  recipeComponents?: RecipeComponent[];
  transcript?: ChatMessage[];
}

export interface BodyMeasurementLogConfig extends WidgetConfigBase {
  kind: "body-measurement-log";
  types: BodyMeasurementTypeKey[]; // ≥1, catalog order
}

export interface BodyMeasurementTrendConfig extends WidgetConfigBase {
  kind: "body-measurement-trend";
  // One measurement per widget: a second measurement means a second
  // instance, which the week grid can then place independently. Optional
  // because sealed week snapshots still carry the pre-split shape below —
  // always read through `trendMeasurementType`.
  measurementType?: BodyMeasurementTypeKey;
  // Absent = auto-scale. Values may be NaN mid-edit; validate blocks Save,
  // so incomplete ranges never persist. Read via `trendAxisRange`.
  axisRange?: BodyMeasurementAxisRange;
  /** @deprecated Pre-split multi-series shape, frozen into sealed weeks. */
  types?: BodyMeasurementTypeKey[];
  /** @deprecated Pre-split multi-series shape, frozen into sealed weeks. */
  axisRanges?: Partial<Record<BodyMeasurementUnit, BodyMeasurementAxisRange>>;
}

// «Magesmerter». The Pain entries themselves live globally per date
// (docs/adr/0028); the config only prices a day that has a Smertenivå.
export interface PainLogConfig extends WidgetConfigBase {
  kind: "pain-log";
  points: number; // > 0, quarter grid
  weekGoal: number;
}

// «Måltider». The Diary meals themselves live globally per date
// (docs/adr/0028); the config only prices a Måltider ferdig day.
export interface MealDiaryConfig extends WidgetConfigBase {
  kind: "meal-diary";
  points: number; // > 0, quarter grid
  weekGoal: number;
}

// «Smertetrend»: weekly average Smertenivå over the rolling 12-week Trend
// window. Reads the global Pain entries; nothing to configure but the title.
export interface PainTrendConfig extends WidgetConfigBase {
  kind: "pain-trend";
}

export type BiologicalSex = "male" | "female";

// The energy-expenditure calculator's inputs, remembered only so it reopens
// populated — nobody should retype their height to re-estimate. Never
// authoritative: `dailyEnergyExpenditure` is the number the widget reads and
// pressing "Bruk" is the only thing that writes it (docs/adr/0012). The two
// are validated independently and deliberately never checked against each
// other: the field is the answer, the calculator is one way of reaching it.
export interface EnergyExpenditureEstimate {
  weightKilograms: number; // finite, > 0
  heightCentimeters: number; // finite, > 0
  // Not an age. An age is a fact with an expiry date: stored as 41 it is
  // wrong within the year and the calculator would keep recomputing from it
  // without saying so. The field on screen is still an age field.
  birthYear: number;
  sex: BiologicalSex;
  activityFactor: number; // one of ACTIVITY_LEVELS' factors
}

export interface MealLogSummaryConfig extends WidgetConfigBase {
  kind: "meal-log-summary";
  // Exactly one meal-log instance. Two Matdagbøker means two Matoversikter:
  // Conditions live per instance, so "days the condition was met" is only
  // ever answerable per instance.
  mealLogId: string;
  // kcal/day the body is assumed to burn. Absent means the card renders the
  // condition table alone and the whole energy block is omitted.
  dailyEnergyExpenditure?: number; // finite, > 0
  expenditureEstimate?: EnergyExpenditureEstimate;
}

export type DayWidgetConfig =
  | RecurringTasksConfig
  | TodoListConfig
  | WorkoutLogConfig
  | CounterConfig
  | MealLogConfig
  | SleepLogConfig
  | WorkdayLogConfig
  | BodyMeasurementLogConfig
  | PainLogConfig
  | MealDiaryConfig;
export type WeekWidgetConfig =
  | WeeklyPointSummaryConfig
  | BodyMeasurementTrendConfig
  | MealLogSummaryConfig
  | PainTrendConfig;
export type WidgetConfig = DayWidgetConfig | WeekWidgetConfig;

// One standing food fact («Smør på brød = 4 g Brelett per skive.») the
// AI estimator applies when a meal description leaves room. Explicit
// detail in the chat always wins over these.
export interface FoodClarification {
  id: string;
  text: string;
}

// A named, reusable ingredient list at reference scale (×1), directly
// editable in the Oppskrifter editor; imported into meals by copy — zero
// AI calls (docs/adr/0005, docs/adr/0006).
export interface Recipe {
  id: string;
  name: string; // non-empty after trim
  ingredients: Ingredient[]; // ≥ 1
  // The measured weight of the finished batch — a denominator for gram
  // amounts only, never applied to the nutrition numbers. Absent ≡ Σ
  // ingredients.grams, which is also what the editor prefills.
  finishedGrams?: number; // finite, > 0
  // How many containers the batch was divided into. Absent ≡ 1, and at 1
  // the «porsjon» unit is hidden — it would equal «oppskrift».
  portions?: number; // whole number ≥ 1
}

export interface SettingsData {
  widgets: WidgetConfig[];
  // Per-user, shared by all meal-log instances, never sealed into week
  // snapshots (docs/adr/0004). Absent ≡ empty.
  foodClarifications?: FoodClarification[];
  // Same live-per-user pattern (docs/adr/0005). Absent ≡ empty.
  recipes?: Recipe[];
}

export interface RecurringTasksDayData {
  checked: Record<string, boolean>;
}

export interface RecurringTasksWeekData {
  checked: Record<string, boolean>;
  // The award day (CONTEXT.md): which day this week's points landed on,
  // already clamped to a day inside the week, so reading it needs no week
  // metadata. Null while the set is unfinished. Absent on weeks written
  // before award days were recorded — those resolve to Sunday, the same
  // clause that handles a set finished after the week was over.
  awardedOn?: DayKey | null;
}

export interface TodoListDayData {
  todos: Todo[];
  // «Ferdig for i dag» (CONTEXT.md): the day is closed. Only ever true of a
  // day with no open item on it — set by the button on an empty list or by
  // the move that carried the last open item away, never by deleting, and
  // dropped the moment an open item appears (docs/adr/0024). Gates nothing.
  // Absent means false, so days and sealed weeks written before it existed
  // need no migration.
  finished?: boolean;
}

export interface WorkoutLogDayData {
  workouts: Workout[];
  // "Hviledag": the user's assertion that there was nothing to log today,
  // which is how a workout-log with no økter still reaches Ferdiglogget.
  // Invariant: restDay === true implies workouts.length === 0 — logging an
  // økt clears it, and deleting that økt again does not restore it.
  // Scores nothing; a rest day is still 0p. Ignored on a Planlagt dag, where
  // rest is planned rather than declared (docs/adr/0025).
  restDay?: boolean;
}

export interface CounterDayData {
  count: number;
  // «Ferdig for i dag» (CONTEXT.md): the day's counting is over before the
  // Daily maximum was reached. Absent means false, so days and sealed weeks
  // written before it existed need no migration. Unlike meal-log's marker of
  // the same name it gates no points — it only reaches Ferdiglogget early.
  finished?: boolean;
}

export interface MealLogDayData {
  meals: Meal[];
  finished: boolean;
}

// The night that ENDED on this day (docs/adr/0008), so `asleepAt` is
// normally the previous evening's clock reading — on Monday, a reading from
// the Sunday before the week even started. Both are bare "HH:MM" wall-clock
// times with no date: nothing here ever reaches into another day's data.
// Absent means not logged, which scores 0.
export interface SleepLogDayData {
  asleepAt?: string;
  awokeAt?: string;
}

// How long the workday was and how it went. A day is entirely described by
// what sits on its own card — nothing here reads across a day or week
// boundary.
export interface WorkdayLogDayData {
  // An "HH:MM" duration, never "00:00": a day with no work in it is said
  // with `notWorked`, so the duration field rejects zero (CONTEXT.md:
  // "Arbeidstid"). Absent means not logged, which scores 0 and never
  // reaches Ferdiglogget.
  duration?: string;
  // "Ikke jobbet": the assertion that there was no work today at all —
  // holiday, time off or illness. Worth 0p, and how such a day still
  // reaches Ferdiglogget. Invariant: notWorked === true implies duration
  // is absent. Logging a duration clears the marker, and clearing that
  // duration again does not restore it.
  notWorked?: boolean;
  // One answer per productivity period, indexed by period number: true =
  // Ja, false = Nei, null or absent = unanswered. Entries past the earned
  // count are ignored by scoring unconditionally and pruned when the
  // duration field settles — the pruning is hygiene, the ignoring is the
  // rule (CONTEXT.md: "Periode").
  periods?: (boolean | null)[];
}

type TodoListWeekData = Record<string, never>;

/** One intended workout on one day of a week's plan (CONTEXT.md). */
export interface PlannedWorkout {
  id: string;
  label: string;
  day: DayKey;
}

export interface WorkoutLogWeekData {
  // Planlagt uke: the day the plan was first saved, already clamped into the
  // week like an award day (docs/adr/0026). Its presence is the marker; it is
  // never cleared and a later save never moves it. Absent = not planned.
  plannedOn?: DayKey;
  // Absent ≡ empty. Order within a day is the order they were added.
  plannedWorkouts?: PlannedWorkout[];
}

type CounterWeekData = Record<string, never>;

type MealLogWeekData = Record<string, never>;

type SleepLogWeekData = Record<string, never>;

type WorkdayLogWeekData = Record<string, never>;

type WeeklyPointSummaryWeekData = Record<string, never>;

// Body measurement widgets keep no runtime data in the week row — the
// data lives in body_measurement rows (docs/adr/0002).
type BodyMeasurementLogDayData = Record<string, never>;

type BodyMeasurementLogWeekData = Record<string, never>;

export interface PainLogDayData {
  // The one scoring fact pain-log keeps in the week: a mirror of whether the
  // date's Pain entry has a Smertenivå. Scoring reads only the week row, so
  // the widget writes this together with every level change and repairs it
  // when the day it shows disagrees with the entry (docs/adr/0028). Absent
  // means false.
  level?: boolean;
  // «Ferdig for i dag»: the day's entry is done, symptoms and note included.
  // Only what makes the card Ferdiglogget — the points come from `level`
  // alone. Needs a level: clearing the level drops it, while changing
  // symptoms or the note afterwards does not. Absent means false.
  finished?: boolean;
}

type PainLogWeekData = Record<string, never>;

export interface MealDiaryDayData {
  // Måltider ferdig (CONTEXT.md), shown as «Ferdig for i dag»: the day's
  // eating is logged. The widget's whole score, and the one scoring fact it
  // keeps in the week (docs/adr/0028). Needs a meal: deleting the day's last
  // meal clears it, while adding or editing meals does not. Absent means
  // false.
  finished?: boolean;
}

type MealDiaryWeekData = Record<string, never>;

type BodyMeasurementTrendWeekData = Record<string, never>;

type PainTrendWeekData = Record<string, never>;

// Everything the Matoversikt shows is derived from the meal-log instance it
// references and from global body measurements — it stores nothing of its own.
type MealLogSummaryWeekData = Record<string, never>;

type WidgetDayData =
  | RecurringTasksDayData
  | TodoListDayData
  | WorkoutLogDayData
  | CounterDayData
  | MealLogDayData
  | SleepLogDayData
  | WorkdayLogDayData
  | BodyMeasurementLogDayData
  | PainLogDayData
  | MealDiaryDayData;
type WidgetWeekData =
  | RecurringTasksWeekData
  | TodoListWeekData
  | WorkoutLogWeekData
  | CounterWeekData
  | MealLogWeekData
  | SleepLogWeekData
  | WorkdayLogWeekData
  | WeeklyPointSummaryWeekData
  | BodyMeasurementLogWeekData
  | PainLogWeekData
  | MealDiaryWeekData
  | BodyMeasurementTrendWeekData
  | PainTrendWeekData
  | MealLogSummaryWeekData;

export interface DayWidgetWeekEntry {
  config: DayWidgetConfig;
  week: WidgetWeekData;
  days: Record<DayKey, WidgetDayData>;
}

export interface WeekWidgetWeekEntry {
  config: WeekWidgetConfig;
  week: WidgetWeekData;
}

export type WidgetWeekEntry = DayWidgetWeekEntry | WeekWidgetWeekEntry;

export interface WeekData {
  weekNumber: number;
  year: number;
  startDate: string;
  state: "open" | "completed";
  widgets: WidgetWeekEntry[];
  completedDays: Partial<Record<DayKey, boolean>>;
}

export const SETTINGS_TYPE = "settings";
export const WEEK_TYPE = "week";

export function settingsId(userId: string): string {
  return `settings:${userId}`;
}

export function weekId(uuid: string): string {
  return `week:${uuid}`;
}

export function emptyDayData(kind?: WidgetConfig["kind"]): WidgetDayData {
  if (kind === "todo-list") return { todos: [] };
  if (kind === "workout-log") return { workouts: [] };
  if (kind === "counter") return { count: 0 };
  if (kind === "meal-log") return { meals: [], finished: false };
  if (kind === "sleep-log") return {};
  if (kind === "body-measurement-log") return {};
  if (kind === "pain-log") return {};
  if (kind === "meal-diary") return {};
  return { checked: {} };
}

function emptyRecurringTasksWeekData(): RecurringTasksWeekData {
  return { checked: {}, awardedOn: null };
}

function emptyWeeklyPointSummaryWeekData(): WeeklyPointSummaryWeekData {
  return {};
}

function emptyTodoListWeekData(): TodoListWeekData {
  return {};
}

function emptyMealLogWeekData(): MealLogWeekData {
  return {};
}

export function emptyWeekData(kind: WidgetConfig["kind"]): WidgetWeekData {
  if (kind === "weekly-point-summary") return emptyWeeklyPointSummaryWeekData();
  if (kind === "todo-list") return emptyTodoListWeekData();
  if (kind === "workout-log") return {};
  if (kind === "counter") return {};
  if (kind === "meal-log") return emptyMealLogWeekData();
  if (kind === "sleep-log") return {};
  if (kind === "body-measurement-log") return {};
  if (kind === "pain-log") return {};
  if (kind === "meal-diary") return {};
  if (kind === "body-measurement-trend") return {};
  if (kind === "meal-log-summary") return {};
  if (kind === "pain-trend") return {};
  return emptyRecurringTasksWeekData();
}

export const DEFAULT_SETTINGS_DATA: SettingsData = { widgets: [] };
